import { io } from 'socket.io-client'
import { API_BASE_URL } from '../config/api'

// Socket base = the API host WITHOUT the /api path suffix — socket.io mounts at
// the server root on `path: '/socket.io'` (see server/server.js).
const SOCKET_URL =
  import.meta.env.VITE_SOCKET_URL || API_BASE_URL.replace(/\/api\/?$/, '')

let socket = null
let socketToken = null // which token the current socket authenticated with
let adminStaffId = null // which staff account this socket belongs to
let adminOwnerId = null // ...or which owner account

// Admin-side Socket.IO singleton. Mirrors the public frontend's client
// (frontend/src/apiservices/authService.js) on purpose, including the transport
// pin: the proxy in front of the API accepts a websocket upgrade and then drops
// the frames, so the socket reports itself connected while every server push is
// lost. Long-polling is the transport that actually delivers.
export const getAdminSocket = (token) => {
  if (!token) return null

  // Same session → reuse. Recreating the socket would tear down listeners other
  // components registered on it.
  if (socket && socketToken === token) {
    if (!socket.connected) socket.connect()
    return socket
  }

  // Different token (re-login, role switch) → start fresh.
  if (socket) {
    socket.disconnect()
    socket = null
  }

  socketToken = token
  socket = io(SOCKET_URL, {
    auth: { token },
    path: '/socket.io',
    transports: ['polling'],
    withCredentials: true,
    forceNew: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  })

  /* Fires on the first connect AND on every reconnect, so the socket always
     (re)joins its `admin_<staffId>` room after a drop. Without this join the
     server's forceLogout push had nowhere to land and an upline lock only
     surfaced when the next request 401'd — i.e. after navigating somewhere. */
  socket.on('connect', () => {
    if (adminStaffId || adminOwnerId) {
      socket.emit('authenticateAdmin', { staffId: adminStaffId, ownerId: adminOwnerId })
    }
  })

  // Upline locked this account (or one above it): the server already revoked
  // the token, this only makes the logout instant. App.jsx owns the redux
  // logout + redirect. The server does not verify the room join, so only act on
  // a push addressed to this socket's own account.
  socket.on('forceLogout', (data) => {
    const mine = (adminStaffId && String(data?.staffId) === adminStaffId)
      || (adminOwnerId && String(data?.ownerId) === adminOwnerId)
    if (!mine) return
    window.dispatchEvent(new CustomEvent('forceLogout', { detail: data }))
  })

  socket.on('connect_error', (error) => {
    console.error('Admin socket connect error:', error?.message || error)
  })

  return socket
}

// Who this admin session is. Set before/after getAdminSocket in either order —
// useOwnerProfileSocket opens the same singleton without an id, so the id is
// kept here rather than passed in, and a socket that is already up is joined
// straight away instead of waiting for the next reconnect.
export const setAdminSessionId = ({ staffId, ownerId } = {}) => {
  adminStaffId = staffId ? String(staffId) : null
  adminOwnerId = ownerId ? String(ownerId) : null
  if ((adminStaffId || adminOwnerId) && socket?.connected) {
    socket.emit('authenticateAdmin', { staffId: adminStaffId, ownerId: adminOwnerId })
  }
}

// ── Owner-profile liveness ──────────────────────────────────────────────────
// Once the socket is actually delivering profile snapshots, the REST endpoint
// must stop being polled — otherwise every route change still fires
// GET /admin/owner/profile and the socket is just extra traffic on top.
// useProfileRefresh consults this and steps aside while the push is healthy;
// if the socket drops (no snapshot for STALE_AFTER_MS) this goes false again
// and the REST path automatically takes over, so a dead socket never leaves the
// dashboard frozen.
const STALE_AFTER_MS = 6000 // 3 missed 2s ticks
let lastOwnerProfileAt = 0

export const markOwnerProfileLive = () => {
  lastOwnerProfileAt = Date.now()
}

export const isOwnerProfileLive = () =>
  lastOwnerProfileAt > 0 && Date.now() - lastOwnerProfileAt < STALE_AFTER_MS

// Ask the server for an immediate tick instead of waiting out the 2s interval.
// Called right after the admin MOVES money, where "in up to 2s" is not good
// enough. Returns false when there is no live socket to ask, so the caller can
// fall back to REST.
export const requestOwnerProfileRefresh = () => {
  if (!socket || !socket.connected || !isOwnerProfileLive()) return false
  socket.emit('refreshOwnerProfile')
  return true
}

export const disconnectAdminSocket = () => {
  if (socket) {
    socket.disconnect()
    socket = null
  }
  socketToken = null
  lastOwnerProfileAt = 0
}

export default getAdminSocket
